import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  CAPACITY_EXPANSION_PRICE,
  BANK_INFO,
  TRANSFER_NOTICE,
  formatPrice,
} from '@/lib/plans';

export default function CapacityStatus({
  academyId,
  studentCount,
  maxStudents,
  onMaxChange,
}: {
  academyId: string;
  studentCount: number;
  maxStudents: number;
  onMaxChange: () => void;
}) {
  const [pendingRequest, setPendingRequest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadPending = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('capacity_requests')
        .select('id')
        .eq('academy_id', academyId)
        .eq('status', 'pending');
      if (err) throw err;
      setPendingRequest((data?.length ?? 0) > 0);
    } catch (e) {
      console.error(e);
    }
  }, [academyId]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const requestExpansion = async () => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const { error: err } = await supabase.rpc('request_capacity_expansion');
      if (err) throw err;
      setSuccess('정원 증설(+100명) 요청을 제출했습니다. 입금 확인 후 운영자가 승인하면 반영됩니다.');
      await loadPending();
      onMaxChange();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(msg || '증설 요청 중 오류가 발생했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const remaining = Math.max(0, maxStudents - studentCount);
  const pct = maxStudents > 0 ? Math.min(100, Math.round((studentCount / maxStudents) * 100)) : 0;
  const isFull = remaining === 0;

  return (
    <div className="bg-white rounded-xl border border-background-200 p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-team-line text-accent-500"></i>
          학생 정원
        </h2>
        {pendingRequest && (
          <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium">
            증설 요청 검토 중
          </span>
        )}
      </div>

      <div className="flex items-end justify-between flex-wrap gap-2 mb-2">
        <div>
          <span className="text-2xl font-bold text-foreground-950">{studentCount}</span>
          <span className="text-sm text-foreground-500"> / {maxStudents}명</span>
        </div>
        <span className={`text-xs font-medium ${isFull ? 'text-red-600' : 'text-foreground-500'}`}>
          {isFull ? '정원이 가득 찼습니다' : `잔여 ${remaining}명`}
        </span>
      </div>

      <div className="h-2.5 rounded-full bg-background-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isFull ? 'bg-red-400' : pct >= 80 ? 'bg-accent-500' : 'bg-primary-500'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <p className="text-xs text-foreground-500 mt-3">
        정원이 가득 차면 학생을 더 등록할 수 없습니다. 100명 단위로 증설을 요청할 수 있으며, 입금 확인
        후 운영자가 승인하면 반영됩니다.
      </p>

      <div className="mt-3 rounded-lg bg-accent-50 border border-accent-200 px-3 py-2.5 text-xs text-foreground-700">
        <div className="flex items-center gap-1.5 font-semibold text-foreground-900">
          <i className="ri-bank-card-line"></i>
          증설 요금 · 입금 계좌
        </div>
        <div className="mt-1">
          <span className="font-semibold text-primary-700">
            +100명 증설 · {formatPrice(CAPACITY_EXPANSION_PRICE)}원
          </span>
        </div>
        <div className="mt-1">
          <span className="font-semibold text-emerald-700">
            {BANK_INFO.bank} {BANK_INFO.account}
          </span>
          <span className="text-foreground-600"> · {BANK_INFO.holder}</span>
        </div>
        <div className="mt-2 pt-2 border-t border-accent-200 flex items-start gap-1.5">
          <i className="ri-alert-line mt-0.5 text-accent-700 flex-shrink-0"></i>
          <span>{TRANSFER_NOTICE}</span>
        </div>
      </div>

      {success && (
        <div className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
          {success}
        </div>
      )}
      {error && (
        <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <button
        onClick={requestExpansion}
        disabled={busy || pendingRequest}
        className="mt-4 whitespace-nowrap inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <i className="ri-add-circle-line"></i>
        {busy ? '요청 중...' : pendingRequest ? '증설 요청 검토 중' : '+100명 증설 요청'}
      </button>
    </div>
  );
}